
































































































































import { defineComponent, reactive, computed } from '@vue/composition-api';
import { useQuery, useResult } from '@vue/apollo-composable';
import { useFormat } from '@ideavivat/vivat-2';

import Progressbar from '@/components/Progressbar.vue';
import Map from '@/components/Map.vue';

import i18n from '@/i18n';

import dashboardQuery from '@/gql/dashboard.gql';
import dayjs from 'dayjs';

import creator from '../table/creator';

const Dashboard = defineComponent({
  setup() {
    const search = reactive({
      text: '',
      type: 'overall',
    });

    const handicraftGroup = ['FirstGroup', 'SecondGroup'];
    const numberAsText = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

    const { result: dashboardResult } = useQuery(dashboardQuery, {
      dateRange: {
        from: dayjs().subtract(30, 'day').format('YYYY-MM-DD HH:mm:ss'),
        to: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      },
    });

    const users = useResult(dashboardResult, { data: [] }, (data) => data?.users);
    const handicraftStatistic = useResult(dashboardResult, {}, (data) => data.handicraftStatistic);
    const mediaTypes = useResult(dashboardResult, [], (data) => data.mediaTypes);
    const creatorGroups = useResult(dashboardResult, [], (data) => data.creatorGroups);
    const learningCenterTypes = useResult(dashboardResult, [], (data) => data.learningCenterTypes);
    const handicraftTypes = useResult(dashboardResult, [], (data) => data.handicraftTypes);
    const handicraftPie = useResult<any, { [key: string]: number }, { [key: string]: number }>(dashboardResult, {
      createHandicraft: -1,
      editHandicraft: -1,
      deleteHandicraft: -1,
    }, (data) => ({
      createHandicraft: parseInt(data.createHandicraft.paginatorInfo.total, 10),
      editHandicraft: parseInt(data.editHandicraft.paginatorInfo.total, 10),
      deleteHandicraft: parseInt(data.deleteHandicraft.paginatorInfo.total, 10),
    }));

    const handicraftPieData = computed(() => ((Object.keys(handicraftPie.value) as ('createHandicraft' | 'editHandicraft' | 'deleteHandicraft')[]).map((k) => ({
      name: { createHandicraft: 'สร้างชิ้นงาน', editHandicraft: 'แก้ไขชิ้นงาน', deleteHandicraft: 'ลบชิ้นงาน' }[k],
      data: handicraftPie.value[k],
      color: { createHandicraft: 'green', editHandicraft: 'yellow', deleteHandicraft: 'red' }[k],
    }))));

    const handicraftGroupSummation = computed(() => {
      const temp = {
        id: 1,
        display: 'จำนวนชิ้นงาน',
        value: parseInt(handicraftStatistic.value.numberOfHandicraftFirstGroup + handicraftStatistic.value.numberOfHandicraftSecondGroup, 10),
      };
      return temp;
    });

    const handicraftGroups = computed(() => {
      const temp = [
        {
          id: 0,
          display: 'งานศิลปาชีพ',
          value: parseInt(handicraftStatistic.value.numberOfHandicraftFirstGroup, 10),
        },
        {
          id: 1,
          display: 'งานหัตถกรรม',
          value: parseInt(handicraftStatistic.value.numberOfHandicraftSecondGroup, 10),
        },
      ];
      return temp;
    });

    const computedGraphValue = computed(() => {
      const temp: any = [];
      for (let j = 0; j < 2; j += 1) {
        const temp2: any = [];
        for (let i = 0; i < numberAsText.length; i += 1) {
          temp2.push(handicraftStatistic.value[`numberOfHandicraft${handicraftGroup[j]}${numberAsText[i]}Type`]);
        }
        temp.push({
          id: j,
          name: j === 0 ? 'งานศิลปาชีพ' : 'งานหัตถกรรม',
          data: temp2,
          color: j === 0 ? 'green' : 'orange',
        });
      }
      return temp;
    });

    const sumKey = (arr: any[], key: string) => arr.map((elm: any) => elm[key]).reduce((a: number, b: number) => a + b, 0);

    const handicraftSummary = computed(() => ({
      id: 1,
      display: 'จำนวนชิ้นงาน',
      value: sumKey(handicraftTypes.value as any[], 'numberOfHandicraftInThisType'),
    }));

    const mediaTypesSummary = computed(() => ({
      id: 2,
      display: 'จำนวนสื่อ',
      value: sumKey(mediaTypes.value as any[], 'numberOfMediaInThisType'),
    }));

    const creatorGroupsSummary = computed(() => ({
      id: 3,
      display: 'จำนวนผู้สร้างสรรค์',
      value: sumKey(creatorGroups.value as any[], 'numberOfCreatorInThisGroup'),
    }));

    const learningCenterTypesSummary = computed(() => ({
      id: 4,
      display: 'จำนวนแหล่งเรียนรู้',
      value: sumKey(learningCenterTypes.value as any[], 'numberOfLearningCenterInThisType'),
    }));

    const overViewValue = computed(() => [
      {
        id: 0,
        display: 'จำนวนข้อมูลทั้งหมด',
        value: handicraftGroupSummation.value.value + mediaTypesSummary.value.value + creatorGroupsSummary.value.value,
      },
      handicraftGroupSummation.value,
      mediaTypesSummary.value,
      creatorGroupsSummary.value,
    ]);

    const getOnlyHandicraftType = computed(() => {
      const temp: any = [];
      // eslint-disable-next-line
      handicraftTypes?.value.forEach((item: any) => {
        temp.push(item.name);
      });
      return temp;
    });

    function useDateTimeFormat(date: any) {
      if (!date) return '';
      return `${useFormat.date(date).displayShortThaiDate()} - ${date.substring(11, 16)}`;
    }

    return {
      i18n,
      search,
      users,
      overViewValue,
      getOnlyHandicraftType,
      computedGraphValue,
      mediaTypesSummary,
      mediaTypes,
      handicraftStatistic,
      handicraftGroupSummation,
      handicraftTypes,
      handicraftGroups,
      creatorGroupsSummary,
      creatorGroups,
      learningCenterTypesSummary,
      learningCenterTypes,
      useDateTimeFormat,
      handicraftPieData,
    };
  },
  components: {
    Progressbar,
    Map,
  },
});

export default Dashboard;
